import { useEffect, useState, useRef } from 'react';

import { useSearchStore } from '@/store/SearchStore';
import { watchPosition } from '@/utils/geolocation';
import { decodePolyline } from '@/utils/maps';
import { useIsScreenSize } from '@/utils/media';

const MIN_INTERSECTION_DISTANCE_METERS = 30;
const POSITION_UPDATE_INTERVAL = 5000;

export const useUserPosition = () => {
  const [userPosition, setUserPosition] = useState<google.maps.LatLngLiteral>();
  const [userHeading, setUserHeading] = useState<number | null>(null);
  const { selectedRoute, activeStep, setActiveStep } = useSearchStore();
  const lastUpdateRef = useRef<number>(0);
  const isMobile = useIsScreenSize('lt-lg');
  const isDesktop = !isMobile;

  useEffect(() => {
    if (!selectedRoute) return;

    // Disable geolocation tracking on desktop devices
    if (isDesktop) return;

    let intervalId: NodeJS.Timeout | null = null;

    const updatePosition = (position: GeolocationPosition) => {
      const now = Date.now();
      lastUpdateRef.current = now;

      const newPosition = { lat: position.coords.latitude, lng: position.coords.longitude };
      setUserPosition(newPosition);
      setUserHeading(position.coords.heading);

      if (selectedRoute) {
        const steps = selectedRoute.legs.flatMap((leg) => leg.steps);
        const userLatLng = new google.maps.LatLng(newPosition.lat, newPosition.lng);

        let closestStepIndex = 0;
        let minDistance = Infinity;

        steps.forEach((step, index) => {
          const startPoint = new google.maps.LatLng(step.start_location.lat(), step.start_location.lng());
          const distanceToStart = google.maps.geometry.spherical.computeDistanceBetween(userLatLng, startPoint);

          let distanceToPath = Infinity;
          if (step.polyline) {
            const path = decodePolyline(step.polyline.points).map(
              (point) => new google.maps.LatLng(point.lat, point.lng),
            );
            for (let i = 0; i < path.length; i++) {
              const distance = google.maps.geometry.spherical.computeDistanceBetween(userLatLng, path[i]);
              if (distance < distanceToPath) {
                distanceToPath = distance;
              }
            }
          }

          const distance = Math.min(distanceToStart, distanceToPath);

          if (distance < minDistance) {
            minDistance = distance;
            closestStepIndex = index;
          }
        });

        if (minDistance < MIN_INTERSECTION_DISTANCE_METERS && closestStepIndex !== activeStep) {
          console.log(`📍 Navigation: Moving to step ${closestStepIndex + 1} - distance: ${minDistance.toFixed(2)}m`);

          if ('vibrate' in navigator) {
            navigator.vibrate(200);
          }

          setActiveStep(closestStepIndex);
        }
      }
    };

    const watchId = watchPosition(updatePosition);

    intervalId = setInterval(() => {
      const timeSinceLastUpdate = Date.now() - lastUpdateRef.current;
      if (timeSinceLastUpdate >= POSITION_UPDATE_INTERVAL) {
        navigator.geolocation.getCurrentPosition(
          updatePosition,
          (error) => {
            console.warn('Failed to get position in interval:', error.message);
          },
          {
            enableHighAccuracy: true,
            timeout: 20000,
            maximumAge: 5000,
          },
        );
      }
    }, POSITION_UPDATE_INTERVAL);

    return () => {
      navigator.geolocation.clearWatch(watchId);
      if (intervalId) clearInterval(intervalId);
    };
  }, [selectedRoute, activeStep, setActiveStep, isDesktop]);

  return { userPosition, userHeading };
};
